import { Button, Tooltip } from '@amzn/meridian';
import { type FC, useEffect, useId, useRef, useState } from 'react';
import { useEffectOnce, usePrevious } from 'react-use';
import { type GenerateAltTextQuery, useGenerateAltTextLazyQuery } from '../../../../generated/graphql';
import { MAX_IMAGE_SIZE_MB } from '../../constants';
import { PromptModal } from '../PromptModal';
import { SparkleIconWithLabel } from '../SparkleIconWithLabel';

export type GenerateAltTextButtonProps = {
  assetId?: string | null;
  locale?: string | null;
  productName?: string;
  imageType: string | null;
  disableGenerate: boolean;
  onNameChange: (name: string) => void;
  onAltTextGenerated: (altText: string) => void;
  onSubmit: () => void;
  onError?: (err: Error) => void;
};

export const GenerateAltTextButton: FC<GenerateAltTextButtonProps> = ({
  assetId,
  locale,
  productName,
  imageType,
  disableGenerate = false,
  onNameChange,
  onAltTextGenerated,
  onSubmit,
  onError,
}) => {
  // We intentionally avoid aborting the network request here to stay consistent with the existing Journey code pattern
  const isAltTextGenerationActiveRef = useRef(false);

  // Ensure any in-flight alt-text generation is deactivated when the component unmounts
  useEffectOnce(() => () => {
    isAltTextGenerationActiveRef.current = false;
  });

  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [promptName, setPromptName] = useState(productName ?? '');

  const prevIsOpen = usePrevious(isPromptOpen);

  useEffect(() => {
    if (!prevIsOpen && isPromptOpen) {
      setPromptName(productName ?? '');
    }
  }, [
    isPromptOpen,
    prevIsOpen,
    productName,
  ]);

  const handleCompleted = (data: GenerateAltTextQuery) => {
    if (!isAltTextGenerationActiveRef.current) return;

    const altText = data.generateAltText?.altText ?? null;

    if (altText !== null && altText !== '') {
      onAltTextGenerated(altText);
    } else {
      onError?.(new Error('Generated alt text is empty. Please try again.'));
    }
  };

  const handleError = (err: Error) => {
    onError?.(err);
  };

  const [generateAltTextQuery, { loading }] = useGenerateAltTextLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleButtonClick = () => {
    if (loading) return;
    setIsPromptOpen(true);
  };

  const handleModalClose = () => {
    isAltTextGenerationActiveRef.current = false;
    setIsPromptOpen(false);
  };

  const handlePromptSubmit = (name: string) => {
    // Runtime guard to ensure assetId is defined (non-null) so the GraphQL input always receives a valid string
    if (!assetId) {
      onError?.(new Error('Image should not be null'));
      return;
    }
    if (!locale) {
      onError?.(new Error('Marketplace or Language should not be null'));
      return;
    }

    isAltTextGenerationActiveRef.current = true;

    setIsPromptOpen(false);
    onSubmit();
    onNameChange(name);

    generateAltTextQuery({
      variables: {
        input: {
          assetId,
          language: locale,
          productName: name.trim(),
        },
      },
    });
  };

  const getDisabledReason = (): string | undefined => {
    if (!assetId) {
      return 'Please upload image before auto-generating alt text';
    }
    if (!locale) {
      return 'Please select language before auto-generating alt text';
    }
    if (disableGenerate) {
      return `${imageType ? imageType.toUpperCase() : 'Large'} images must be under ${MAX_IMAGE_SIZE_MB} MB to auto-generate alt text`;
    }
    if (loading) {
      return 'Generating alt text... please wait';
    }
    return;
  };
  const tooltipId = `${useId()}-alttext-tooltip`;

  const disabledReason = getDisabledReason();

  const btn = (
    <Button
      aria-describedby={disabledReason ? tooltipId : undefined}
      disabled={disableGenerate || !assetId || !locale || loading}
      onClick={handleButtonClick}
      size="small"
      type="tertiary"
    >
      {loading ? 'Generating...' : <SparkleIconWithLabel label="Auto-Generate" />}
    </Button>
  );
  return (
    <>
      {disabledReason ? (
        <Tooltip id={tooltipId} position="end" title={disabledReason}>
          {btn}
        </Tooltip>
      ) : (
        btn
      )}
      <PromptModal
        loading={loading}
        onCancel={handleModalClose}
        onNameChange={(name) => {
          setPromptName(name);
          onNameChange(name);
        }}
        onSubmit={handlePromptSubmit}
        open={isPromptOpen}
        productName={promptName}
      />
    </>
  );
};
